/**
 * Pure helpers for the tenancy-centric Mieterübersicht.
 *
 * No React, no fetch — every function here is a plain transformation of the
 * `GET /api/admin/tenancies` item shape, so the rules below can be tested
 * directly rather than through a rendered page.
 *
 * Three of these rules exist because getting them wrong is silent:
 *
 * * **Grouping keys off `tenancy.unit_id`, never off a units lookup.**
 *   `fetchAdminUnits()` sends no parameters and therefore receives the
 *   server default `limit=50` (routes_admin_units.py). A join against that
 *   array drops every unit past position 50 into an unlabelled bucket while
 *   still rendering rows — the worst kind of wrong. The tenancy row already
 *   carries `unit_id`, `unit_title` and `unit_short_unit_id`, so the group is
 *   both keyed and labelled without the lookup existing at all.
 *
 * * **`display_end_date` is the move-out date.** It is derived server-side as
 *   actual → termination_effective → contractual (tenancy_lifecycle.py). The
 *   three raw fields are present in the response and deriving from them here
 *   would fork that rule into the frontend.
 *
 * * **A cancelled tenancy has no move-out date to show.** Nobody moved in, so
 *   nobody moved out — but `display_end_date` is populated for these rows
 *   anyway, and every shape it takes is misleading. The row is therefore
 *   rendered without an end date. Display only: the API field and the stored
 *   `move_out_date` are untouched, and the occupancy/exclusion queries that
 *   depend on them are unaffected.
 *
 * * **`monthly_revenue_equivalent` is revenue, not rent, and it is 0.0 for
 *   any tenancy that is not active or reserved** — a status fallback, not a
 *   measured zero. `revenueDisplayValue` returns null there so the cell can
 *   render an em dash instead of a CHF 0.00 that reads like a fact.
 */

/** Roles that are NOT the primary tenant, in the order the API sorts them. */
const CO_TENANT_ROLES = ["co_tenant", "solidarhafter"];

const PARTICIPANT_ROLE_LABELS_DE = {
  primary_tenant: "Hauptmieter",
  co_tenant: "Co-Mieter",
  solidarhafter: "Solidarhafter",
};

/** Raw tenancy statuses for which the revenue equivalent is a measured value. */
const REVENUE_BEARING_STATUSES = ["active", "reserved"];

/**
 * The one display status for which an end date is not a fact about the tenancy.
 * See the suppression in `buildTenancyRow`.
 */
const CANCELLED_DISPLAY_STATUS = "cancelled";

export function participantRoleLabelDe(role) {
  return PARTICIPANT_ROLE_LABELS_DE[role] || null;
}

/**
 * Initials for the avatar circle. Two letters from first + last word, one
 * word falls back to its first two characters.
 */
export function initialsFrom(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

/**
 * Swiss franc formatting: apostrophe thousands separator, always two
 * decimals — `CHF 1'480.00`. de-CH already uses the apostrophe.
 */
export function formatChf(value) {
  const amount = Number(value || 0);
  return `CHF ${amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** A participant counts as current unless it was explicitly ended/cancelled. */
function isCurrentParticipant(p) {
  const status = String(p?.status ?? "active").toLowerCase();
  return status === "active" || status === "planned" || status === "";
}

function personFromParticipant(p) {
  const t = p?.tenant || {};
  const name =
    t.name ||
    [t.first_name, t.last_name].filter(Boolean).join(" ").trim() ||
    "";
  return {
    tenantId: p?.tenant_id != null ? String(p.tenant_id) : null,
    name,
    email: t.email || null,
    // `tenant_summary_dict` is id/first_name/last_name/email/name/phone. The
    // phone joined it after the rest, so it is read defensively: an older
    // response has no key here and the cell then omits the line rather than
    // rendering an empty one.
    phone: String(t.phone || "").trim() || null,
    role: p?.role || null,
    roleLabel: participantRoleLabelDe(p?.role),
  };
}

/**
 * Split a tenancy's participants into the one primary tenant and the rest.
 *
 * The primary falls back to the flat `primary_tenant_display_name` /
 * `primary_tenant_email` / `primary_tenant_phone` the API resolves centrally
 * (primary_tenant_display), so a legacy tenancy with an empty participants
 * table still shows a person — now with their number too.
 */
export function splitParticipants(tenancy) {
  const all = Array.isArray(tenancy?.participants) ? tenancy.participants : [];
  const current = all.filter(isCurrentParticipant);

  const primaryParticipant = current.find((p) => p?.role === "primary_tenant");
  const coTenants = current
    .filter((p) => CO_TENANT_ROLES.includes(p?.role))
    .map(personFromParticipant);

  const primary = primaryParticipant
    ? personFromParticipant(primaryParticipant)
    : {
        tenantId:
          tenancy?.tenant_id != null ? String(tenancy.tenant_id) : null,
        name: tenancy?.primary_tenant_display_name || "",
        email: tenancy?.primary_tenant_email || null,
        phone: String(tenancy?.primary_tenant_phone || "").trim() || null,
        role: "primary_tenant",
        roleLabel: PARTICIPANT_ROLE_LABELS_DE.primary_tenant,
      };

  return { primary, coTenants };
}

/**
 * The monthly revenue equivalent, or null when the number would only be the
 * status-driven 0.0 fallback rather than a measured value.
 */
export function revenueDisplayValue(tenancy) {
  const raw = String(tenancy?.status ?? "").toLowerCase();
  if (!REVENUE_BEARING_STATUSES.includes(raw)) return null;
  const value = tenancy?.monthly_revenue_equivalent;
  if (value == null) return null;
  return Number(value);
}

/**
 * One display row per tenancy.
 *
 * `roomsById` is optional and used only for the room area — `GET
 * /api/admin/rooms` takes no skip/limit and returns every room of the org, so
 * unlike the units endpoint it can be joined against safely.
 */
export function buildTenancyRow(tenancy, roomsById) {
  const { primary, coTenants } = splitParticipants(tenancy);
  const roomId = tenancy?.room_id != null ? String(tenancy.room_id) : null;
  const room = roomId && roomsById ? roomsById.get(roomId) : null;
  const sizeM2 = room?.size_m2 ?? null;
  const displayStatus = tenancy?.display_status || null;

  // A tenancy cancelled before move-in has no move-out: nobody moved in, so
  // nobody moved out. `display_end_date` is populated for these rows regardless,
  // and production carries all three of its shapes — a synthetic
  // `move_in_date - 1` from the migration-111 backfill, a NULL, and an
  // untouched contractual end years out. None of them is an Auszug, and the
  // first reads as an exit the day BEFORE the entry.
  //
  // Suppressed here, in the shared viewmodel, rather than in the cells: the
  // wide table and the narrow card both render `row.displayEndDate`, so one
  // rule keeps the two layouts from answering differently. And it is keyed on
  // the SAME `displayStatus` the badge renders, so a row can never show
  // "Vor Einzug storniert" next to a move-out date.
  //
  // The move-IN date deliberately survives: it says when the tenancy was
  // planned, which is real and is the useful half of the pair.
  const suppressEndDate = displayStatus === CANCELLED_DISPLAY_STATUS;

  return {
    id: String(tenancy?.id ?? ""),
    tenantId: primary.tenantId,
    unitId: tenancy?.unit_id != null ? String(tenancy.unit_id) : null,
    unitTitle: tenancy?.unit_title || null,
    unitShortId: tenancy?.unit_short_unit_id || null,
    referenceNumber: tenancy?.reference_number || null,
    primary,
    coTenants,
    roomName: tenancy?.room_name || null,
    roomSizeM2: sizeM2 == null ? null : Number(sizeM2),
    moveInDate: tenancy?.move_in_date || null,
    // Canonical move-out (tenancy_lifecycle.tenancy_display_end_date), except
    // on a cancelled tenancy — see `suppressEndDate` above.
    displayEndDate: suppressEndDate ? null : tenancy?.display_end_date || null,
    displayStatus,
    // The SERVER's answer to "is this contract live today", passed through
    // untouched. Never recomputed here from the dates: the same rule already
    // exists in `tenancy_is_running_on`, in the SQL clause behind the `active`
    // filter, and in `portfolio_map_service` — a fourth copy in JavaScript
    // would be the one that drifts, and it would drift silently, because a row
    // has no way to notice it disagrees with the filter that put it on screen.
    // `isTenancyActiveByDates` is deliberately not used for this.
    //
    // Defaults to `false`, not `true`: a payload without the field renders one
    // pill instead of wrongly asserting a contract is running.
    isRunning: tenancy?.is_running === true,
    revenueEquivalent: revenueDisplayValue(tenancy),
  };
}

/**
 * Group rows by `tenancy.unit_id`, preserving the order units first appear in
 * the result set so the grouping never reorders what the server sorted.
 *
 * A row whose `unit_id` is missing lands in a single explicit "Ohne Unit"
 * group rather than being dropped.
 */
export function groupRowsByUnit(rows) {
  const groups = new Map();
  for (const row of rows || []) {
    const key = row.unitId || "__none__";
    if (!groups.has(key)) {
      groups.set(key, {
        unitId: row.unitId,
        title: row.unitTitle || row.unitShortId || "Ohne Unit",
        subtitle: row.unitTitle && row.unitShortId ? row.unitShortId : null,
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  }
  return Array.from(groups.values());
}

/**
 * Label for the count pill in a unit header.
 *
 * With more than one page loaded, the rows of a unit can be split across
 * pages, so the number describes the current page and says so. On a single
 * page it is the unit's full count and is worded as such.
 */
export function unitCountLabel(count, { paginated } = {}) {
  const noun = count === 1 ? "Mietverhältnis" : "Mietverhältnisse";
  return paginated ? `${count} ${noun} auf dieser Seite` : `${count} ${noun}`;
}
